'use client';

import { useFinancialStore } from '@/store/useFinancialStore';
import GlassCard from '@/components/GlassCard';
import GlassButton from '@/components/GlassButton';
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';

export default function AssistantPage() {
  const { signals } = useFinancialStore();
  const [query, setQuery] = useState('');

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary selection:bg-brand-teal/30">
      {/* Header */}
      <div className="border-b border-border-primary bg-bg-secondary/50 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-xl tracking-tight font-semibold text-text-primary">
              Neural Assistant
            </h1>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-brand-teal-light rounded-full animate-pulse shadow-glow"></div>
              <span className="text-xs font-bold tracking-widest uppercase text-text-secondary">AI Core Online</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-4xl mx-auto space-y-8 md:space-y-12">
          {/* Welcome Message */}
          <div className="text-center space-y-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="inline-block p-4 rounded-3xl bg-brand-teal/10 border border-brand-teal/20 mb-2 md:mb-4"
            >
              <div className="w-12 h-12 md:w-16 md:h-16 rounded-full border-2 border-brand-teal/40 border-t-brand-teal animate-spin shadow-glow" />
            </motion.div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-text-primary">
              How can I optimize your <span className="text-brand-teal-light text-glow-teal">Alpha?</span>
            </h2>
            <p className="text-sm md:text-lg text-text-secondary max-w-xl mx-auto font-light leading-relaxed">
              I am your sovereign intelligence companion. Query the network, analyze trends, or request protocol adjustments.
            </p>
          </div>

          {/* Central Query Hub */}
          <GlassCard variant="elevated" className="p-2 md:p-3 rounded-[2.5rem] bg-bg-secondary/40 border-white/5 shadow-2xl relative group">
            <div className="absolute inset-0 bg-brand-teal/5 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-1000 blur-2xl -z-10" />
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 md:gap-4 p-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Query market vectors or ask for a strategy audit..."
                className="flex-1 bg-transparent border-none text-text-primary focus:ring-0 px-4 md:px-6 py-4 md:py-5 text-sm md:text-lg font-medium placeholder:text-text-tertiary placeholder:font-light"
              />
              <GlassButton variant="primary" className="rounded-2xl px-6 md:px-12 h-14 md:h-16 uppercase tracking-widest font-black text-xs shadow-glow">
                Transmit Query
              </GlassButton>
            </div>
          </GlassCard>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              { icon: '🛰️', title: 'Neural Scan', desc: 'Sovereign-grade multi-factor vector analysis.' },
              { icon: '🛡️', title: 'Risk Guard', desc: 'Real-time structural exposure audit.' },
              { icon: '🎯', title: 'Vector ID', desc: 'High-conviction signal intercept detection.' }
            ].map((action, i) => (
              <GlassCard key={i} variant="subtle" className="p-5 md:p-6 cursor-pointer hover:border-brand-teal/30 transition-all duration-300 group relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-teal/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="text-center">
                  <div className="text-2xl md:text-3xl mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-500">{action.icon}</div>
                  <h3 className="text-[10px] md:text-xs font-black text-text-primary mb-2 uppercase tracking-widest">{action.title}</h3>
                  <p className="text-[10px] md:text-[11px] text-text-secondary leading-relaxed font-bold tracking-tight">{action.desc}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}